import styled from "@emotion/styled";
import ons from "onsenui";
import React, { useEffect, useState } from "react";
import * as Ons from "react-onsenui";
import {
  NursingQuestionAnswerType,
  NursingQuestionPossibleAnswer,
  NursingQuestionVBPAnswer,
  NursingQuestionVBPWithAnswer,
} from "../../../../messages/VBP";
import { STRINGS } from "../../../../strings";
import { translate } from "../../../../translate";
import { CHECK_ICON } from "../../../../utils/svgs_new";
import { AppButton } from "../../../Generic/AppButton";
import { AppIcon } from "../../../Generic/AppIcon";
import AppSlideFadeIn from "../../../Generic/AppSlideFadeIn";
import { If } from "../../../Generic/If";
import VBPQuestionExtraDetails from "./VBPQuestionExtraDetails/VBPQuestionExtraDetails";
import VBPQuestionDropDown from "./VBPQuestionTypes/VBPQuestionDropDown/VBPQuestionDropDown";
import VBPRadioQuestion from "./VBPQuestionTypes/VBPRadioQuestion/VBPRadioQuestion";
import VBPYesNoQuestion from "./VBPQuestionTypes/VBPYesNoQuestion/VBPYesNoQuestion";

const DEFAULT_ICON_URL = "https://cdn.iconscout.com/icon/free/png-256/medicine-2130805-1798585.png";
interface Props {
  showAnsweredIndication: boolean;
  nursingQuestionVBPWithAnswer: NursingQuestionVBPWithAnswer;
  onVBPQuestionChange: (nursingQuestionVBPWithAnswer: NursingQuestionVBPWithAnswer) => void;
  onVBPQuestionAnswer: (nursingQuestionVBPAnswer: NursingQuestionVBPAnswer) => Promise<void>;
  onVBPQuestionNotesClick: (vbpQustionNotesAreOpen: boolean) => void;
}

const VBPQuestionCard = (props: Props) => {
  const answerDetails = props.nursingQuestionVBPWithAnswer.answerDetails;
  const [isVBPContentOpen, setVBPContentOpen] = useState(false);
  const [isSubmittingVBPQuestionAnswer, setSubmittingVBPQuestionAnswer] = useState(false);

  useEffect(() => {
    switch (props.nursingQuestionVBPWithAnswer.answerType) {
      case NursingQuestionAnswerType.YES_NO: {
        setVBPContentOpen(false);
        break;
      }

      default: {
        if (answerDetails === null) {
          setVBPContentOpen(false);
          break;
        } else {
          const answer = props.nursingQuestionVBPWithAnswer.possibleAnswers.find(
            (possibleAnswer) => possibleAnswer.text === answerDetails.answer
          );
          if (answer !== undefined) {
            setVBPContentOpen(answer.isTriggeringAlert);
            break;
          }
          setVBPContentOpen(false);
          break;
        }
      }
    }
  }, [
    answerDetails,
    props.nursingQuestionVBPWithAnswer.answerType,
    props.nursingQuestionVBPWithAnswer.possibleAnswers,
  ]);

  const getVBPItemAnswerFromQuestion = React.useCallback((): NursingQuestionVBPAnswer => {
    let answerDetailsObject: NursingQuestionVBPAnswer | null =
      props.nursingQuestionVBPWithAnswer.answerDetails;
    if (answerDetailsObject === null) {
      answerDetailsObject = {
        answer: "",
        notes: null,
        riskLevel: 1,
        nursingQuestionId: props.nursingQuestionVBPWithAnswer.id,
      };
    }
    return answerDetailsObject;
  }, [props.nursingQuestionVBPWithAnswer.answerDetails, props.nursingQuestionVBPWithAnswer.id]);

  const handleVBPQuestionAnswer = React.useCallback(() => {
    if (props.nursingQuestionVBPWithAnswer.answerDetails === null) {
      return;
    }

    setSubmittingVBPQuestionAnswer(true);
    props.onVBPQuestionAnswer
      .call(null, props.nursingQuestionVBPWithAnswer.answerDetails)
      .then(() => {
        if (!props.showAnsweredIndication) {
          ons.notification.toast("Success", {
            timeout: 500,
          });
        }
      })
      .finally(() => {
        setSubmittingVBPQuestionAnswer(false);
      });
  }, [
    props.nursingQuestionVBPWithAnswer.answerDetails,
    props.onVBPQuestionAnswer,
    props.showAnsweredIndication,
  ]);

  const handleAnswerClick = React.useCallback(
    (nursingQuestionPossibleAnswer: NursingQuestionPossibleAnswer) => {
      const newAnswerDetails = getVBPItemAnswerFromQuestion();
      newAnswerDetails.answer = nursingQuestionPossibleAnswer.text;
      props.onVBPQuestionChange.call(null, {
        ...props.nursingQuestionVBPWithAnswer,
        answerDetails: newAnswerDetails,
      });

      setVBPContentOpen(nursingQuestionPossibleAnswer.isTriggeringAlert);
      handleVBPQuestionAnswer();
    },
    [getVBPItemAnswerFromQuestion, handleVBPQuestionAnswer, props]
  );

  const handleYesNoAnswerClick = React.useCallback(
    (selectedAnswer: string) => {
      const newAnswerDetails = getVBPItemAnswerFromQuestion();
      newAnswerDetails.answer = selectedAnswer;
      props.onVBPQuestionChange({
        ...props.nursingQuestionVBPWithAnswer,
        answerDetails: newAnswerDetails,
      });
      handleVBPQuestionAnswer();
    },
    [getVBPItemAnswerFromQuestion, handleVBPQuestionAnswer, props]
  );

  const handleSetQuestionRisk = (newRiskLevel: number) => {
    const newAnswerDetails = getVBPItemAnswerFromQuestion();
    newAnswerDetails.riskLevel = newRiskLevel;
    props.onVBPQuestionChange({
      ...props.nursingQuestionVBPWithAnswer,
      answerDetails: newAnswerDetails,
    });
  };

  const handleSetQuestionNotes = (notes: any) => {
    const newAnswerDetails = getVBPItemAnswerFromQuestion();
    newAnswerDetails.notes = notes;
    props.onVBPQuestionChange({
      ...props.nursingQuestionVBPWithAnswer,
      answerDetails: newAnswerDetails,
    });
  };

  const possibleAnswers = props.nursingQuestionVBPWithAnswer.possibleAnswers;
  const notes =
    answerDetails === null ? null : answerDetails.notes === null ? null : answerDetails.notes;
  const riskLevel = answerDetails === null ? null : answerDetails.riskLevel;
  const answer = answerDetails === null ? null : answerDetails.answer;
  const iconUrl =
    props.nursingQuestionVBPWithAnswer.iconUrl === null
      ? DEFAULT_ICON_URL
      : props.nursingQuestionVBPWithAnswer.iconUrl;

  const renderVBPQuestion = React.useCallback(() => {
    switch (props.nursingQuestionVBPWithAnswer.answerType) {
      case NursingQuestionAnswerType.YES_NO: {
        return <VBPYesNoQuestion answer={answer} onQuestionClick={handleYesNoAnswerClick} />;
      }

      case NursingQuestionAnswerType.RADIO: {
        return (
          <VBPRadioQuestion
            selectedValue={answer}
            possibleAnswers={possibleAnswers}
            onQuestionClick={handleAnswerClick}
          />
        );
      }

      case NursingQuestionAnswerType.DROPDOWN: {
        return (
          <VBPQuestionDropDown
            answer={answer}
            possibleAnswers={possibleAnswers}
            onQuestionClick={handleAnswerClick}
          />
        );
      }

      default: {
        return null;
      }
    }
  }, [
    answer,
    handleAnswerClick,
    handleYesNoAnswerClick,
    possibleAnswers,
    props.nursingQuestionVBPWithAnswer.answerType,
  ]);

  return (
    <div className="VBPQuestionCard">
      <div className="VBPQuestionCard__vbpQuestionContent">
        <div className="VBPQuestionCard__questionDisplay">
          <div className="VBPQuestionCard__questionIconBox">
            <img src={iconUrl} />
          </div>
          <span>{props.nursingQuestionVBPWithAnswer.name}</span>
          <If condition={answer !== "" && props.showAnsweredIndication}>
            <VBPQuestionCard.CheckIcon icon={CHECK_ICON} size={14}></VBPQuestionCard.CheckIcon>
          </If>
        </div>

        {renderVBPQuestion()}
        <If condition={isVBPContentOpen} comment="is vbp content open">
          <AppSlideFadeIn>
            <VBPQuestionExtraDetails
              riskLevel={riskLevel}
              notes={notes}
              onSetQuestionRisk={handleSetQuestionRisk}
              onSetQuestionNotes={handleSetQuestionNotes}
              onVBPQuestionNotesClick={props.onVBPQuestionNotesClick}
            />
          </AppSlideFadeIn>
        </If>
        <If condition={isVBPContentOpen} comment="is vbp content open">
          <div className="submitQuestionContainer">
            {isSubmittingVBPQuestionAnswer ? (
              <Ons.ProgressCircular indeterminate={true} />
            ) : (
              <AppButton color="primary" onClick={handleVBPQuestionAnswer}>
                <span>{translate(STRINGS.DONE)}</span>
              </AppButton>
            )}
          </div>
        </If>
      </div>
    </div>
  );
};

VBPQuestionCard.CheckIcon = styled(AppIcon)`
  margin-left: auto;
`;

export default React.memo(VBPQuestionCard);
